import { FC, useState } from 'react';
import { Button, Form, Modal } from 'antd';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { useCreateAbsenceMutation } from 'app/services/edpApiV1/absence';
import { TDivisionEmployees } from 'shared/types/types';
import { getCreateAbsenceDate } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TAbsenceForm } from 'shared/types/absence.types';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import ModalComment from './modalComment';
import EmployeeInput from './employeeInput';
import RangeInput from './rangeInput';
import style from './index.module.scss';

type TProps = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const ModalAddOneAbsence: FC<TProps> = ({ isModalOpen, setIsModalOpen }) => {
  const [form] = Form.useForm<TAbsenceForm>();
  const [employee, setEmployee] = useState<TDivisionEmployees | null>(null);
  const [range, setRange] = useState<string[] | null>(null);
  const [comment, setComment] = useState<string>('');

  const [getEmployees, resultEmployees] = useLazyGetDivisionEmployeesQuery();
  const [createAbsenceTrigger, createResult] = useCreateAbsenceMutation();
  const { isSuccess, isError, reset, error } = createResult;
  const isFullForm = employee && range && comment;

  const resetStates = () => {
    setEmployee(null);
    setRange(null);
    setComment('');
  };

  const onChangeEmployee = () => {
    if (range) {
      form.setFieldsValue({
        period: undefined,
      });
      setRange(null);
    }
    if (comment) {
      form.setFieldsValue({
        comment: '',
      });
      setComment('');
    }
  };
  const onChangeRange = () => {
    if (comment) {
      form.setFieldsValue({
        comment: '',
      });
      setComment('');
    }
  };
  // функции для сброса зависимых полей, если значение меняется end
  const handleCancel = () => {
    form.resetFields();
    resetStates();
    setIsModalOpen(false);
  };

  const onFinish = async () => {
    if (range && employee) {
      const startedAt = getCreateAbsenceDate(range[0]);
      const endedAt = getCreateAbsenceDate(range[1]);
      const newAbsence = {
        employeeId: employee.id,
        startedAt,
        endedAt,
        comment,
      };
      await createAbsenceTrigger(newAbsence);
    }
    // очистка формы и сброс стейта
    handleCancel();
  };

  useNotifications(
    isSuccess,
    isError,
    'Неявка успешно создана',
    returnErrorOrMessage(error, 'Ошибка при создании неявки'),
    reset,
  );

  return (
    <Modal
      title="Заполните поля для создания неявки"
      footer={false}
      onCancel={handleCancel}
      visible={isModalOpen}
      bodyStyle={{ padding: '40px' }}
      width={700}
      destroyOnClose
    >
      <Form form={form} name="createOneAbsence" onFinish={onFinish} layout="vertical">
        <EmployeeInput
          data={resultEmployees.data}
          getEmployees={getEmployees}
          setEmployee={setEmployee}
          onChangeEmployee={onChangeEmployee}
          divisionName={employee?.division}
        />
        <RangeInput disabled={!employee} setRange={setRange} onChangeRange={onChangeRange} />
        <ModalComment disabled={!range} setComment={setComment} />
        <Form.Item className={style.subBtn}>
          <Button htmlType="submit" type="primary" disabled={!isFullForm} loading={createResult.isLoading}>
            Создать
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalAddOneAbsence;
